"use client";

import {
  NeoButton,
  NeoDatePicker,
  NeoFileUpload,
  NeoSelect,
  NeoTextBox
} from "@/components/forms";
import {NeoError} from "@/components/shared";
import {NeoSwitchInput} from "@/components/shared/neo-switch/neo-switch-input";
import {useNeoContext} from "@/context/neo/neo.context";
import {CustomerType} from "@/libs/enums/live";
import {parseUserDate} from "@/libs/helper";
import {userService} from "@/service/users.service";
import {yupResolver} from "@hookform/resolvers/yup";
import {SelectItem} from "@nextui-org/react";
import {useRouter} from "next/navigation";
import {useRef, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {AiOutlineLoading} from "react-icons/ai";
import Select from "react-select";
import * as yup from "yup";

interface IUserForm {
  name: string;
  dateOfBirth?: Date | null;
  gender?: any;
  parentName: string;
  phone: string;
  secondaryNumber?: string | null;
  email?: string | null;
  rfid?: boolean | null;
  uhf?: boolean | null;
  rfidNo?: number | null;
  uhfId?: string | null;
  admissionNo: string;
  standard: string;
  section?: string | null;
  pickup?: any;
  drop?: any;
}

type UserFormProps = {
  items: any;
  client: Client;
  onCancel?: () => void;
  onSuccess?: () => void;
  hideVehicleMappingFields?: boolean;
};

export const UserForm: React.FC<UserFormProps> = ({
  items,
  client,
  onCancel,
  onSuccess,
  hideVehicleMappingFields = false
}) => {
  const selectedClient = useRef(client);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(items?.data);
  const {push} = useRouter();
  const {vehicles, filterContext} = useNeoContext();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /*const [activeRfidStatus, setActiveRfidStatus] = useState<boolean>(
    items?.data?.rfid
  );*/
  const [activeRfidStatus, setActiveRfidStatus] = useState<boolean>(true);
  const [activeUhfStatus, setActiveUhfStatus] = useState<boolean>(true);

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  //const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showRfid, setshowRfidl] = useState(false);
  const [showUhf, setshowUhf] = useState(false);
  const formatDate = (date: Date | null | undefined): string | null => {
    if (!date) return null;

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const validationSchema = (
    activeRfidStatus: boolean,
    shouldValidateVehicleMapping: boolean
  ) =>
    yup.object().shape({
      name: yup
        .string()
        .min(2, "Enter valid name")
        .max(25, "Enter valid name")
        .required("Please enter name"),
      dateOfBirth: yup
        .date()
        .nullable()
        .typeError("Please select date of birth")
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        })
        .test(
          "dob-not-in-future",
          "Date of birth cannot be in the future.",
          value => {
            if (!value) return true;

            const selectedDate = new Date(value);
            selectedDate.setHours(0, 0, 0, 0);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            return selectedDate <= today;
          }
        ),
      gender: yup.string().nullable(),
      parentName: yup.string().required("Please enter parent name"),
      phone: yup
        .string()
        .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
        .required("Please enter phone number"),
      secondaryNumber: yup
        .string()
        .nullable()
        .notRequired()
        .test(
          "is-valid-optional-phone",
          "Phone number must be exactly 10 digits",
          value => {
            if (!value || value.trim() === "") return true; // allow empty
            return /^\d{10}$/.test(value); // validate if not empty
          }
        ),
      email: yup.string().nullable(),
      rfid: yup.boolean().nullable(),
      rfidNo: yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
          String(originalValue).trim() === "" ? null : value
        )
        .typeError("RFID number must be a number"),
      uhf: yup.boolean().nullable(),

      uhfId: yup
        .string()
        .nullable()
        .transform((value, originalValue) =>
          String(originalValue).trim() === "" ? null : value
        )
        .max(24, "UHF ID must be at most 24 characters")
        .matches(
          /^[A-Za-z0-9]+$/,
          "UHF ID can contain only uppercase letters, lowercase letters, and numbers without spaces"
        ),
      admissionNo: yup.string().required("Please enter admission no"),
      standard: yup.string().required("Please enter class"),
      section: yup.string().nullable(),
      pickup: shouldValidateVehicleMapping
        ? yup
            .array()
            .of(yup.string())
            .max(4, "You can only select up to 4 drop vehicles")
            .test(
              "pickup-or-drop",
              "Please select at least one (Pickup or Drop)",
              function (pickup) {
                const drop = this.parent.drop || [];
                return (pickup?.length || 0) > 0 || (drop?.length || 0) > 0;
              }
            )
        : yup.array().of(yup.string()).nullable(),
      drop: shouldValidateVehicleMapping
        ? yup
            .array()
            .of(yup.string())
            .max(4, "You can only select up to 4 drop vehicles")
            .test(
              "pickup-or-drop",
              "Please select either pickup or drop vehicle",
              function (drop) {
                const pickup = this.parent.pickup || [];
                return (drop?.length || 0) > 0 || (pickup?.length || 0) > 0;
              }
            )
        : yup.array().of(yup.string()).nullable()
    });
  const {
    register,
    handleSubmit,
    formState: {isSubmitting, errors, touchedFields},
    control,
    setError,
    setValue,
    trigger,
    clearErrors
  } = useForm<IUserForm>({
    defaultValues: {
      ...userData,
      dateOfBirth: parseUserDate(userData?.dob),
      rfid: userData?.rfid ?? false,
      uhf: userData?.uhf ?? false,
      uhfId: userData?.uhfId ?? "",
      pickup: userData?.pickup?.map((vehicle: any) => vehicle?.trackerid) || [], // Store as an array
      drop: userData?.drop?.map((vehicle: any) => vehicle?.trackerid) || [] // Store as an array
    },
    mode: "onChange",
    resolver: yupResolver(
      validationSchema(activeRfidStatus, !hideVehicleMappingFields)
    )
  });

  const showVehicleError =
    (touchedFields.pickup || touchedFields.drop) && errors.pickup?.message;

  const onDateChange = (name: keyof IUserForm, event: Date | null) => {
    clearErrors(name);
    setValue(name, event, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  };

  const handleSwitchChange = (name: "rfid" | "uhf", value: boolean) => {
    setValue(name, value);
    if (name === "rfid") {
      setActiveRfidStatus(value);
    } else {
      setActiveUhfStatus(value);
    }
    if (!value && name === "rfid") {
      setValue("rfidNo", undefined);
      clearErrors("rfidNo");
    }
    if (!value && name === "uhf") {
      setValue("uhfId", "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
      clearErrors("uhfId");
    }
  };
  const onSubmit = async (data: IUserForm) => {
    const additionalPrimaryData = {
      insId: selectedClient.current.institutionId,
      trackerId: items?.trackerId,
      currentUser: selectedClient.current.institutionUserId
    };
    var jpegFile: any = "";
    if (uploadedFile) {
      const jpegBlob = await convertToJPEG(uploadedFile);
      jpegFile = new File([jpegBlob], "converted.jpeg", {
        type: "image/jpeg"
      });
    }
    var rfid: any = "";
    if (data.rfidNo) {
      rfid = data.rfidNo;
    }
    const uhf = String(data.uhfId ?? "").trim();
    const pickup = hideVehicleMappingFields
      ? userData?.pickup?.map((vehicle: any) => vehicle?.trackerid) || []
      : data?.pickup;
    const drop = hideVehicleMappingFields
      ? userData?.drop?.map((vehicle: any) => vehicle?.trackerid) || []
      : data?.drop;
    const vehicles: {trackerId: string; type: number}[] = [];
    if (typeof pickup === "string" && pickup) {
      pickup.split(",").forEach(id => {
        const pickuptracker = {
          trackerId: id,
          type: 1
        };
        vehicles.push(pickuptracker);
      });
    } else if (Array.isArray(pickup) && pickup.length > 0) {
      pickup.forEach(id => {
        const pickuptracker = {
          trackerId: id,
          type: 1
        };
        vehicles.push(pickuptracker);
      });
    }

    if (typeof drop === "string" && drop) {
      drop.split(",").forEach(id => {
        const droptracker = {
          trackerId: id,
          type: 2
        };
        vehicles.push(droptracker);
      });
    } else if (Array.isArray(drop) && drop.length > 0) {
      drop.forEach(id => {
        const droptracker = {
          trackerId: id,
          type: 2
        };
        vehicles.push(droptracker);
      });
    }

    var vehicledatas: any = [];
    if (vehicles) {
      const mergedMap = new Map<string, Set<number>>();
      vehicles.forEach(({trackerId, type}) => {
        if (!mergedMap.has(trackerId)) {
          mergedMap.set(trackerId, new Set());
        }
        mergedMap.get(trackerId)!.add(type);
      });

      vehicledatas = Array.from(mergedMap.entries()).map(
        ([trackerId, types]) => {
          if (types.has(1) && types.has(2)) {
            return {trackerId, type: 3};
          } else {
            return {trackerId, type: Array.from(types)[0]};
          }
        }
      );
    }
    var institutionUserId: string = "";
    var institutionId: any = "";
    if (selectedClient?.current?.institutionUserId) {
      institutionUserId = selectedClient?.current?.institutionUserId;
    }
    if (selectedClient?.current?.institutionId) {
      institutionId = selectedClient?.current?.institutionId;
    }
    const formData = new FormData();
    formData.append("loginUserId", institutionUserId);
    formData.append("insId", institutionId);
    formData.append("name", data.name);
    formData.append("gender", data.gender ?? "");
    formData.append("parentName", data.parentName);
    formData.append("phone", data.phone);
    formData.append("uniqueId", data?.admissionNo);
    formData.append("email", data.email ?? "");
    formData.append("dob", formatDate(data.dateOfBirth) ?? "");
    formData.append("userClass", data.standard);
    formData.append("userSection", data.section ?? "");
    formData.append("rfid", String(Boolean(data.rfid)));
    formData.append("rfidNo", rfid);
    formData.append("uhf", String(Boolean(data.uhf) && Boolean(uhf)));
    formData.append("uhfId", uhf);
    formData.append("secondaryNumber", data.secondaryNumber ?? "");
    if (vehicledatas) {
      vehicledatas.forEach((item: any, index: any) => {
        formData.append(`vehicles[${index}].trackerId`, item.trackerId);
        formData.append(`vehicles[${index}].type`, String(item.type));
      });
    }
    if (isImageChanged()) {
      formData.append("image", jpegFile);
    }

    if (items?.userId) {
      formData.append("userId", items?.userId);
    }
    /*for (const [key, value] of formData.entries()) {
      console.log(key, value);
    }
    console.log(isImageChanged());
    return false;*/
    setLoading(true);
    if (items?.userId) {
      await userService
        .onUpdateUserDetails(
          items?.userId,
          additionalPrimaryData,
          data,
          formData
        )
        .then(async (response: any) => {
          if (response?.data?.status === "success") {
            if (onSuccess) {
              onSuccess();
            } else {
              push(`/users`);
            }
          } else {
            if (response?.data?.errors?.length > 0) {
              response?.data?.errors?.forEach(
                ({field, message}: {field: any; message: string}) => {
                  setError(field, {type: "server", message: message});
                }
              );
            }
            if (response?.data?.message) {
              setErrorMessage(response?.data?.message);
            }
          }
        })
        .catch(err => {
          if (err?.errors?.length > 0) {
            err?.errors?.forEach(
              ({field, message}: {field: any; message: string}) => {
                setError(field, {type: "server", message: message});
              }
            );
          }
        })
        .finally(() => {
          setLoading(false);
          setTimeout(() => {
            setErrorMessage(null);
          }, 2000);
        });
    } else {
      await userService
        .onCreateUserDetails(additionalPrimaryData, formData)
        .then(async (response: any) => {
          if (response?.data?.status === "success") {
            if (onSuccess) {
              onSuccess();
            } else {
              push(`/users`);
            }
          } else {
            if (response?.data?.errors?.length > 0) {
              response?.data?.errors?.forEach(
                ({field, message}: {field: any; message: string}) => {
                  setError(field, {type: "server", message: message});
                }
              );
            }
            if (response?.data?.errors?.length == 0) {
              setErrorMessage(response?.data?.message);
            }
          }
        })
        .catch(err => {
          if (err?.errors?.length > 0) {
            err?.errors?.forEach(
              ({field, message}: {field: any; message: string}) => {
                setError(field, {type: "server", message: message});
              }
            );
          }
        })
        .finally(() => {
          setLoading(false);
          setTimeout(() => {
            setErrorMessage(null);
          }, 2000);
        });
    }
  };
  const genderList: {label: string; value: number}[] = [
    {label: "Male", value: 1},
    {label: "Female", value: 2}
  ];
  const userList: {label: string; value: string}[] = [
    {label: "Student", value: "1"},
    {label: "Employee", value: "2"}
  ];
  const handleFileChange: (file: File | null) => void = file => {
    if (file) {
      setUploadedFile(file);
      /*if(items?.userId){
        setOriginalFile(file);
      }*/
    } else {
      setUploadedFile(null);
    }
  };
  const isImageChanged = () => {
    // Case 1: No original file but a new one is uploaded
    if (!originalFile && uploadedFile) return true;

    // Case 2: Original file exists but new file is removed
    if (originalFile && !uploadedFile) return false;

    // Case 3: Both exist → compare details
    if (originalFile && uploadedFile) {
      return (
        originalFile.name !== uploadedFile.name ||
        originalFile.size !== uploadedFile.size ||
        originalFile.lastModified !== uploadedFile.lastModified
      );
    }

    // Case 4: Nothing changed
    return false;
  };
  const convertToJPEG = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0);
          canvas.toBlob(
            blob => {
              if (blob) resolve(blob);
              else reject("JPEG conversion failed");
            },
            "image/jpeg",
            0.9
          );
        };
        img.onerror = reject;
        img.src = reader.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="w-full px-5"
      autoComplete="off"
    >
      <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-[auto,1fr]">
        <div className="flex items-start justify-start">
          <div className="grid w-full grid-cols-1 gap-1">
            <div className="col-span-1 py-2 lg:col-span-1">
              <NeoFileUpload
                onChange={handleFileChange}
                defaultImage={userData?.profileImage}
              />
              {/*previewUrl && <img src={previewUrl} alt="JPEG preview" />*/}
            </div>
          </div>
        </div>
        <div className="grid w-full grid-cols-4 gap-5">
          {/* Name Field*/}
          <div className="col-span-4 py-2 lg:col-span-1">
            <NeoTextBox
              label="Name"
              id="name"
              isRequired
              defaultValue={userData?.name}
              placeholder="Please enter name"
              color={errors.name ? "danger" : "default"}
              isInvalid={errors.name ? true : false}
              errorMessage={errors.name && `${errors.name.message}`}
              {...register("name")}
            />
          </div>

          {/* Gender Field*/}
          <div className="col-span-4 py-2 lg:col-span-1">
            <Controller
              name="gender"
              control={control}
              render={({field}) => (
                <NeoSelect
                  {...field}
                  label="Gender"
                  id="gender"
                  placeholder="Please select gender"
                  color={errors.gender ? "danger" : "default"}
                  isInvalid={errors.gender ? true : false}
                  errorMessage={errors.gender && `${errors.gender.message}`}
                  {...register("gender")}
                  defaultSelectedKeys={[userData?.gender]}
                >
                  {genderList.map((e: any) => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label}
                    </SelectItem>
                  ))}
                </NeoSelect>
              )}
            />
          </div>

          {/* DOB  Field*/}
          <div className="col-span-4 py-2 lg:col-span-1">
            <Controller
              name="dateOfBirth"
              control={control}
              render={({field: {ref, value, onChange, ...fieldRest}}) => (
                <NeoDatePicker
                  {...fieldRest}
                  label="Date of Birth"
                  placeholder="YYYY-MM-DD"
                  inputClassName="placeholder:text-sm placeholder:text-gray-500"
                  errorMessage={
                    errors.dateOfBirth && `${errors.dateOfBirth.message}`
                  }
                  value={value}
                  handleDateChange={(e: any) => {
                    onChange(e);
                    onDateChange("dateOfBirth", e);
                  }}
                />
              )}
            />
          </div>
          {/* Parent Name Field*/}
          <div className="col-span-4 py-2 lg:col-span-1">
            <NeoTextBox
              label="Parent Name"
              id="parentName"
              isRequired
              defaultValue={userData?.parentName}
              placeholder="Please enter parent name"
              color={errors.parentName ? "danger" : "default"}
              isInvalid={errors.parentName ? true : false}
              errorMessage={errors.parentName && `${errors.parentName.message}`}
              {...register("parentName")}
            />
          </div>

          {/* Phone Number Field*/}
          <div className="col-span-4 py-2 lg:col-span-1">
            <label className="mb-1 flex gap-1">
              Phone Number
              <span className="flex items-start text-red-500">*</span>
            </label>
            <div className="flex w-full max-w-xs overflow-hidden rounded-medium border-2">
              <span className="flex items-center border-r border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700">
                +91
              </span>
              <NeoTextBox
                classNames={{
                  base: "w-full", // ensures it grows properly
                  inputWrapper: "border-none shadow-none px-0 bg-transparent",
                  input: "text-sm px-3 py-2"
                }}
                id="phone"
                isRequired
                defaultValue={userData?.phone}
                placeholder="Please enter mobile number"
                color={errors.phone ? "danger" : "default"}
                isInvalid={!!errors.phone}
                errorMessage={errors.phone?.message}
                {...register("phone")}
                variant="flat" // optionally disable border entirely inside
                radius="none"
                fullWidth
              />
            </div>
          </div>

          {/* Phone Number Field*/}
          <div className="col-span-4 py-2 lg:col-span-1">
            <label className="mb-1 block">Secondary Number</label>
            <div className="flex w-full max-w-xs overflow-hidden rounded-medium border-2">
              <span className="flex items-center border-r border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700">
                +91
              </span>
              <NeoTextBox
                classNames={{
                  base: "w-full", // ensures it grows properly
                  inputWrapper: "border-none shadow-none px-0 bg-transparent",
                  input: "text-sm px-3 py-2"
                }}
                id="secondaryNumber"
                defaultValue={userData?.secondaryNumber}
                placeholder="Please enter mobile number"
                color={errors.secondaryNumber ? "danger" : "default"}
                isInvalid={!!errors.secondaryNumber}
                errorMessage={errors.secondaryNumber?.message}
                {...register("secondaryNumber")}
                variant="flat" // optionally disable border entirely inside
                radius="none"
                fullWidth
              />
            </div>
          </div>

          {/* Email Field*/}
          <div className="col-span-4 py-2 lg:col-span-1">
            <NeoTextBox
              label="Email"
              id="email"
              defaultValue={userData?.email}
              placeholder="Please enter email"
              color={errors.email ? "danger" : "default"}
              isInvalid={errors.email ? true : false}
              errorMessage={errors.email && `${errors.email.message}`}
              {...register("email")}
            />
          </div>

          {/* Admission No Field*/}
          <div className="col-span-4 py-2 lg:col-span-1">
            <NeoTextBox
              label={
                filterContext?.client?.customerType ===
                CustomerType.EMPLOYEETRACKING
                  ? "Employee ID"
                  : "Admission No"
              }
              id="admissionNo"
              isRequired
              defaultValue={userData?.admissionNo}
              placeholder="Please enter admissionNo"
              color={errors.admissionNo ? "danger" : "default"}
              isInvalid={errors.admissionNo ? true : false}
              errorMessage={
                errors.admissionNo && `${errors.admissionNo.message}`
              }
              {...register("admissionNo")}
            />
          </div>

          {/* Standard Field*/}
          <div className="col-span-4 py-2 lg:col-span-1">
            <NeoTextBox
              //  label="Class"
              label={
                filterContext?.client?.customerType ===
                CustomerType.EMPLOYEETRACKING
                  ? "Department"
                  : "Class"
              }
              id="standard"
              isRequired
              defaultValue={userData?.className}
              placeholder="Please enter standard"
              color={errors.standard ? "danger" : "default"}
              isInvalid={errors.standard ? true : false}
              errorMessage={errors.standard && `${errors.standard.message}`}
              {...register("standard")}
            />
          </div>

          {/* Section Field*/}
          {filterContext?.client?.customerType !==
            CustomerType.EMPLOYEETRACKING && (
            <div className="col-span-4 py-2 lg:col-span-1">
              <NeoTextBox
                label="Section"
                id="section"
                defaultValue={userData?.sectionName}
                placeholder="Please enter section"
                color={errors.section ? "danger" : "default"}
                isInvalid={errors.section ? true : false}
                errorMessage={errors.section && `${errors.section.message}`}
                {...register("section")}
              />
            </div>
          )}

          {/* Emergency  Contact Field*/}
          {/* <div className="col-span-4 py-2 lg:col-span-1">
            <NeoTextBox
              label="Emergency Contact No"
              id="emergencyContactNo"
              placeholder="Please enter emergency contact"
              color={errors.emergencyContactNo ? "danger" : "default"}
              isInvalid={errors.emergencyContactNo ? true : false}
              errorMessage={
                errors.emergencyContactNo &&
                `${errors.emergencyContactNo.message}`
              }
              {...register("emergencyContactNo")}
            />
          </div>*/}

          {/* Destination Field*/}
          {/* <div className="col-span-4 py-2 lg:col-span-1">
            <NeoTextBox
              label="Destination"
              id="destination"
              defaultValue={userData?.destination}
              placeholder="Please enter destination"
              color={errors.destination ? "danger" : "default"}
              isInvalid={errors.destination ? true : false}
              errorMessage={errors.destination && `${errors.destination.message}`}
              {...register("destination")}
            />
          </div> */}

          {/* Pickup Vehicle*/}
          {!hideVehicleMappingFields && (
            <div className="col-span-4 py-2 lg:col-span-1">
              {/* <Controller
              name="pickup"
              control={control}
              render={({field}) => (
                <NeoSelect
                  {...field}
                  label="Pickup"
                  id="pickup"
                  selectionMode="multiple"
                  placeholder="Please enter pickup vehicle"
                  color={errors.pickup ? "danger" : "default"}
                  isInvalid={errors.pickup ? true : false}
                  errorMessage={errors.pickup && `${errors.pickup.message}`}
                  {...register("pickup")}
                  defaultSelectedKeys={userData?.pickup?.map(
                    (vehicle: any) => vehicle?.trackerid
                  )}
                >
                  {vehicles?.map((e: any) => (
                    <SelectItem key={e.trackerId} value={e.trackerId}>
                      {e.trackerName}
                    </SelectItem>
                  ))}
                </NeoSelect>
              )}
            /> */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="pickup"
                  className="flex gap-1 text-sm font-medium text-gray-700"
                >
                  Pickup{" "}
                  <span className="flex items-start text-red-500">*</span>
                </label>
                <Controller
                  name="pickup"
                  control={control}
                  render={({field}) => {
                    const selectedValues = vehicles.filter(v =>
                      field.value?.includes(v.trackerId)
                    );

                    return (
                      <div>
                        <Select
                          {...field}
                          isMulti
                          options={vehicles}
                          aria-label="Pickup Vehicle"
                          getOptionLabel={e => e.trackerName}
                          getOptionValue={e => e.trackerId}
                          value={selectedValues}
                          // onChange={(selected) => field.onChange(selected)}
                          closeMenuOnSelect={false}
                          menuPortalTarget={
                            typeof document !== "undefined"
                              ? document.body
                              : undefined
                          }
                          menuPosition="fixed"
                          {...(!items?.userId && {maxMenuHeight: 160})}
                          onChange={selected => {
                            // field.onChange(selected.map(item => item.trackerId))
                            const value = selected.map(item => item.trackerId);
                            field.onChange(value);
                            trigger(["pickup", "drop"]); // ✅ revalidate both
                          }}
                          placeholder="Select pickup vehicle"
                          styles={{
                            control: (base: any) => ({
                              ...base,
                              minHeight: "20px",
                              borderWidth: "2px",
                              borderColor: showVehicleError
                                ? "#dc2626"
                                : "#d4d4d8",
                              borderRadius: "12px",
                              paddingLeft: "8px",
                              paddingRight: "8px",
                              fontSize: "14px",
                              backgroundColor: "white",
                              boxShadow: "none"
                            }),
                            menu: (base: any) => ({
                              ...base,
                              zIndex: 9999
                            }),

                            valueContainer: (base: any) => ({
                              ...base,
                              maxHeight: "100px",
                              overflowY: "auto",
                              flexWrap: "wrap"
                            }),
                            multiValue: (base: any) => ({
                              ...base,
                              backgroundColor: "white",
                              border: "1px solid #d4d4d8",
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center"
                            }),
                            multiValueLabel: (base: any) => ({
                              ...base,
                              color: "#131313"
                            }),
                            multiValueRemove: (base: any) => ({
                              ...base,
                              color: "#131313",
                              float: "right",
                              ":hover": {
                                backgroundColor: "#d4d4d8",
                                color: "#131313"
                              }
                            }),
                            option: (base: any, state: any) => ({
                              ...base,
                              backgroundColor: state.isFocused
                                ? "#d4d4d8"
                                : "white",
                              color: "#000",
                              cursor: "pointer"
                            })
                          }}
                        />
                        {showVehicleError && (
                          <p className="text-sm text-red-600">
                            {errors.pickup?.message as string}
                          </p>
                        )}
                      </div>
                    );
                  }}
                />
              </div>
              {/* {errors.pickup &&
              typeof errors.pickup === "object" &&
              "message" in errors.pickup &&
              typeof errors.pickup.message === "string" && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.pickup.message}
                </p>
              )} */}
            </div>
          )}

          {/* Drop Vehicle*/}
          {!hideVehicleMappingFields && (
            <div className="col-span-4 py-2 lg:col-span-1">
              {/* <Controller
              name="drop"
              control={control}
              render={({field}) => (
                <NeoSelect
                  {...field}
                  label="Drop"
                  id="drop"
                  selectionMode="multiple"
                  placeholder="Please enter drop vehicle"
                  color={errors.drop ? "danger" : "default"}
                  isInvalid={errors.drop ? true : false}
                  defaultSelectedKeys={userData?.drop?.map(
                    (vehicle: any) => vehicle?.trackerid
                  )}
                  errorMessage={errors.drop && `${errors.drop.message}`}
                  {...register("drop")}
                >
                  {vehicles?.map((e: any) => (
                    <SelectItem key={e.trackerId} value={e.trackerId}>
                      {e.trackerName}
                    </SelectItem>
                  ))}
                </NeoSelect>
              )}
            /> */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="drop"
                  className="flex gap-1 text-sm font-medium text-gray-700"
                >
                  Drop <span className="flex items-start text-red-500">*</span>
                </label>
                <Controller
                  name="drop"
                  control={control}
                  render={({field}) => {
                    const selectedValues = vehicles.filter(v =>
                      field.value?.includes(v.trackerId)
                    );

                    return (
                      <div>
                        <Select
                          {...field}
                          isMulti
                          options={vehicles}
                          aria-label="Drop Vehicle"
                          getOptionLabel={e => e.trackerName}
                          getOptionValue={e => e.trackerId}
                          value={selectedValues}
                          // onChange={(selected) => field.onChange(selected)}
                          closeMenuOnSelect={false}
                          menuPortalTarget={
                            typeof document !== "undefined"
                              ? document.body
                              : undefined
                          }
                          menuPosition="fixed"
                          {...(!items?.userId && {maxMenuHeight: 160})}
                          onChange={selected => {
                            // field.onChange(selected.map(item => item.trackerId))
                            const value = selected.map(item => item.trackerId);
                            field.onChange(value);
                            trigger(["pickup", "drop"]); // ✅ revalidate both
                          }}
                          placeholder="Select drop vehicle"
                          styles={{
                            control: (base: any) => ({
                              ...base,
                              minHeight: "20px",
                              borderWidth: "2px",
                              borderColor: showVehicleError
                                ? "#dc2626"
                                : "#d4d4d8",
                              borderRadius: "12px",
                              paddingLeft: "8px",
                              paddingRight: "8px",
                              fontSize: "14px",
                              backgroundColor: "white",
                              boxShadow: "none"
                            }),
                            menu: (base: any) => ({
                              ...base,
                              zIndex: 9999
                            }),

                            valueContainer: (base: any) => ({
                              ...base,
                              maxHeight: "100px",
                              overflowY: "auto",
                              flexWrap: "wrap"
                            }),
                            multiValue: (base: any) => ({
                              ...base,
                              backgroundColor: "white",
                              border: "1px solid #d4d4d8",
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center"
                            }),
                            multiValueLabel: (base: any) => ({
                              ...base,
                              color: "#131313"
                            }),
                            multiValueRemove: (base: any) => ({
                              ...base,
                              color: "#131313",
                              float: "right",
                              ":hover": {
                                backgroundColor: "#d4d4d8",
                                color: "#131313"
                              }
                            }),
                            option: (base: any, state: any) => ({
                              ...base,
                              backgroundColor: state.isFocused
                                ? "#d4d4d8"
                                : "white",
                              color: "#000",
                              cursor: "pointer"
                            })
                          }}
                        />
                        {showVehicleError && (
                          <p className="text-sm text-red-600">
                            {errors.pickup?.message as string}
                          </p>
                        )}
                      </div>
                    );
                  }}
                />
              </div>
              {/* {errors.drop &&
              typeof errors.drop === "object" &&
              "message" in errors.drop &&
              typeof errors.drop.message === "string" && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.drop.message}
                </p>
              )} */}
            </div>
          )}

          {/* RFID*/}
          <div
            className={`col-span-4 py-2 lg:col-span-1 ${showRfid ? "" : "hidden"}`}
          >
            <NeoSwitchInput
              defaultSelected={userData?.rfid ? userData.rfid : false}
              className="!mt-0"
              label="RFID"
              activeLabel="Yes"
              inActiveLabel="No"
              // errors={errors.rfid && `${errors.rfid.message}`}
              onValueChange={value => handleSwitchChange("rfid", value)}
            />
          </div>

          {/* RFID No*/}
          {activeRfidStatus && (
            <div className="col-span-4 py-2 lg:col-span-1">
              <NeoTextBox
                label="RFID No"
                id="rfidNo"
                defaultValue={userData?.rfidNo}
                placeholder="Please enter RFID no"
                color={errors.rfidNo ? "danger" : "default"}
                isInvalid={errors.rfidNo ? true : false}
                errorMessage={errors.rfidNo && `${errors.rfidNo.message}`}
                {...register("rfidNo")}
              />
            </div>
          )}

          {/* UHF */}
          <div
            className={`col-span-4 py-2 lg:col-span-1 ${showUhf ? "" : "hidden"}`}
          >
            <NeoSwitchInput
              defaultSelected={userData?.uhf ? userData.uhf : false}
              className="!mt-0"
              label="UHF"
              activeLabel="Yes"
              inActiveLabel="No"
              // errors={errors.uhf && `${errors.uhf.message}`}
              onValueChange={value => handleSwitchChange("uhf", value)}
            />
          </div>

          {/* UHF No*/}
          {activeUhfStatus && (
            <div className="col-span-4 py-2 lg:col-span-1">
              <NeoTextBox
                label="UHF No"
                id="uhfId"
                defaultValue={userData?.uhfId}
                placeholder="Please enter UHF no"
                color={errors.uhfId ? "danger" : "default"}
                isInvalid={errors.uhfId ? true : false}
                errorMessage={errors.uhfId && `${errors.uhfId.message}`}
                {...register("uhfId", {
                  setValueAs: value => String(value ?? "").trim()
                })}
              />
            </div>
          )}

          {/* <NeoDatePicker
            label="Start Date"
            placeholder="Please enter Medical Start Date"
            // value={new Date(data?.medTestExpiry)}
            errorMessage={errors.startDate?.message}
            handleDateChange={e => onDateChange("medTestExpiry", e)}
          />
           <NeoDatePicker
            label="End Date"
            placeholder="Please enter End Date"
            // value={new Date(data?.medTestExpiry)}
            errorMessage={errors.endDAte?.message}
            handleDateChange={e => onDateChange("medTestExpiry", e)}
          /> */}

          {/* User Role*/}
          {/* <div className="col-span-4 py-2 lg:col-span-1">
            <NeoSelect
              label="User Role"
              id="userRole"
              placeholder="Please select userrole"
              color={errors.userRole ? "danger" : "default"}
              isInvalid={errors.userRole ? true : false}
              errorMessage={errors.userRole && `${errors.userRole.message}`}
              {...register("userRole")}
            />
          </div> */}

          {/* User Type*/}
          {/*<div className="col-span-4 hidden py-2 lg:col-span-1">
            <Controller
              name="gender"
              control={control}
              render={({field}) => (
                <NeoSelect
                  {...field}
                  label="User Type"
                  id="userType"
                  placeholder="Please select usertype"
                  color={errors.userType ? "danger" : "default"}
                  isInvalid={errors.userType ? true : false}
                  errorMessage={errors.userType && `${errors.userType.message}`}
                  {...register("userType")}
                  defaultSelectedKeys={userData?.userType}
                >
                  {userList.map((e: any) => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label}
                    </SelectItem>
                  ))}
                </NeoSelect>
              )}
            />
          </div>*/}
        </div>
      </div>
      {errorMessage ? <NeoError message={errorMessage}></NeoError> : null}
      <div className="flex items-center justify-end p-4">
        <NeoButton
          className="mt-2"
          type="button"
          disabled={isSubmitting}
          isLoading={loading}
          spinner={
            <AiOutlineLoading size="xs" className="animate-spin h-6 w-6" />
          }
          color="white"
          onPress={() => {
            if (onCancel) {
              onCancel();
            } else {
              push(`/users`);
            }
          }}
        >
          Cancel
        </NeoButton>
        <NeoButton
          className="mx-5 mt-2"
          type="submit"
          disabled={isSubmitting}
          isLoading={loading}
          spinner={
            <AiOutlineLoading size="xs" className="animate-spin h-6 w-6" />
          }
          color="black"
        >
          Save
        </NeoButton>
      </div>
    </form>
  );
};
